import { useMemo, useState } from "react";
import { ScrollView } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import { Text, View } from "@src/components";
import type { RoadSensePayload } from "@visioni/a3s-reports/client-messages";
import { RoadSenseHero } from "./RoadSenseHero";
import { TrafficSummary } from "./TrafficSummary";
import { RoadSenseSiteMap } from "./RoadSenseSiteMap";
import { TrafficTrendChart } from "./TrafficTrendChart";
import { TrafficFilters } from "./TrafficFilters";
import {
  applyTrafficFilters,
  defaultTrafficFilters,
  instantsDayBounds,
  movementOptions,
  spanDays,
  type TrafficFilters as TrafficFiltersValue,
} from "../filters/traffic-filters";

interface RoadSenseReportViewProps {
  payload: RoadSensePayload;
  /**
   * ISO instant the report row was last generated (the row's `generatedAt`),
   * surfaced in the hero's "Generated At" freshness line. Threaded from the
   * fetch hook — undefined in the dev preview (no live row).
   */
  generatedAt?: string;
  /**
   * Controlled trend filters. When provided (the live report screen drives
   * these from URL params so a filtered view is shareable), the view is fully
   * controlled. When omitted (the dev preview), the view manages its own
   * filter state locally.
   */
  filters?: TrafficFiltersValue;
  onFiltersChange?: (next: TrafficFiltersValue) => void;
}

/** Fixed height for the map pane so the report doesn't stretch with site count. */
const INSIGHTS_PANEL_HEIGHT = 420;

/**
 * The assembled RoadSense report body, rendered purely from a decoded
 * `RoadSensePayload`. Kept free of any data-fetching so it can be driven by the
 * live hook (the report screen) or a static fixture (the dev preview screen)
 * through the same code path.
 *
 * RoadSense's section set is deliberately smaller than WasteSense's: hero, KPI
 * summary, then the filtered trend chart paired with the site map. No incident
 * list, no evidence images, no per-site event logs.
 */
export function RoadSenseReportView({ payload, generatedAt, filters, onFiltersChange }: RoadSenseReportViewProps) {
  // Uncontrolled fallback: the dev preview has no URL to hang filter state on,
  // so the view holds it locally, seeded with the data-aware default granularity
  // (lazy init so the payload's span is read once). The live screen passes
  // `filters`/`onChange` and owns this from the URL instead.
  const [localFilters, setLocalFilters] = useState<TrafficFiltersValue>(() =>
    defaultTrafficFilters(spanDays(payload.trend.instants)),
  );
  const activeFilters = filters ?? localFilters;
  const handleFiltersChange = onFiltersChange ?? setLocalFilters;

  const { trend, showWeekendShading } = useMemo(
    () => applyTrafficFilters(payload.trend, activeFilters),
    [payload.trend, activeFilters],
  );

  const dateBounds = useMemo(() => instantsDayBounds(payload.trend.instants), [payload.trend.instants]);
  const movements = useMemo(() => movementOptions(payload.trend), [payload.trend]);

  // The trend chart is the primary artefact here, so it gets the dominant
  // column. The map is a secondary locator — and when the report carries no
  // coordinates at all it'd otherwise render a half-width "no location data"
  // placeholder that just steals room from the chart, so in that case we drop
  // it and let the chart span the full width.
  const hasMapPoints = useMemo(
    () => payload.scenarios.some((s) => Number.isFinite(s.map_lat) && Number.isFinite(s.map_lon)),
    [payload.scenarios],
  );

  return (
    <View gap="6xl">
      <RoadSenseHero meta={payload.report_meta} generatedAt={generatedAt} siteTimezone={payload.site.site_timezone} />
      <TrafficSummary cards={payload.traffic_summary} />
      <View gap="md">
        <Text textStyle="md/semibold" color="text">
          Traffic insights
        </Text>
        {dateBounds ?
          <TrafficFilters
            filters={activeFilters}
            onChange={handleFiltersChange}
            minDate={dateBounds.min}
            maxDate={dateBounds.max}
            spanDays={spanDays(payload.trend.instants)}
            movements={movements}
          />
        : null}
        <View flexDirection="row" gap="md" flexWrap="wrap" alignItems="stretch">
          {hasMapPoints ?
            <View flex={2} minWidth={280} height={INSIGHTS_PANEL_HEIGHT}>
              <RoadSenseSiteMap site={payload.site} scenarios={payload.scenarios} minHeight={INSIGHTS_PANEL_HEIGHT} />
            </View>
          : null}
          <View flex={3} minWidth={280} height={INSIGHTS_PANEL_HEIGHT}>
            <ChartScroller>
              <TrafficTrendChart
                trend={trend}
                showWeekendShading={showWeekendShading}
                // A subset only when *some but not all* movements are picked.
                // Selecting every chip individually equals "All" (the dashed line
                // still sums everything), so it must read "All", not "Sum".
                movementsAreSubset={
                  activeFilters.movements.length > 0 && activeFilters.movements.length < movements.length
                }
              />
            </ChartScroller>
          </View>
        </View>
      </View>
    </View>
  );
}

/**
 * Keeps the trend chart legible on narrow screens. Below {@link CHART_MIN_WIDTH}
 * the chart would squash its axis labels into an unreadable smear, so instead of
 * shrinking we let the plot hold that minimum width and scroll horizontally. On
 * wider panes `flexGrow` stretches the content to fill the column, so the scroll
 * only ever engages when it's actually needed.
 */
function ChartScroller({ children }: { children: React.ReactNode }) {
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator
      style={styles.chartScroll}
      contentContainerStyle={styles.chartScrollContent}
    >
      <View style={styles.chartScrollInner}>{children}</View>
    </ScrollView>
  );
}

/** Below this the chart scrolls horizontally rather than squashing its labels. */
const CHART_MIN_WIDTH = 360;

const styles = StyleSheet.create(() => ({
  chartScroll: {
    flex: 1,
  },
  chartScrollContent: {
    flexGrow: 1,
  },
  chartScrollInner: {
    flex: 1,
    minWidth: CHART_MIN_WIDTH,
    height: "100%",
  },
}));
